<template>
  <div class="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
    <!-- Loading State -->
    <div v-if="isLoadingEvent" class="rounded-3xl border border-slate-100 bg-white px-6 py-10 text-center shadow-md">
      <div class="inline-flex items-center gap-2 text-slate-600">
        <span class="h-4 w-4 rounded-full border-2 border-slate-600 border-t-transparent animate-spin"></span>
        Loading event...
      </div>
    </div>

    <!-- Event Not Found -->
    <div v-else-if="!event" class="rounded-3xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center space-y-4">
      <div class="text-6xl">❌</div>
      <h4 class="text-xl font-semibold text-slate-900">Event Not Found</h4>
      <p class="text-slate-500">The event you're looking for doesn't exist or you don't have access to it.</p>
      <router-link to="/events" class="inline-flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-primary-700 transition">Back to Events</router-link>
    </div>

    <!-- Event Detail -->
    <div v-else class="space-y-6">
      <!-- Event Header -->
      <header class="rounded-3xl bg-white border border-slate-100 px-6 py-6 shadow-lg flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div class="min-w-0 flex-1">
          <nav class="text-sm text-slate-500 mb-2">
            <router-link to="/events" class="hover:text-primary-600">Events</router-link>
            <span class="mx-2">/</span>
            <span class="text-slate-900">{{ event.title }}</span>
          </nav>
          <h1 class="text-3xl font-semibold text-slate-900">{{ event.title }}</h1>
          <div
            v-if="event.description"
            class="event-detail-description rich-text-content mt-2 text-sm text-slate-500"
            v-html="event.description"
          ></div>
          <div class="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
            <span class="inline-flex items-center gap-2"><i class="fas fa-calendar"></i> {{ formatDate(event.date) }}</span>
            <span v-if="event.venue" class="inline-flex items-center gap-2"><i class="fas fa-map-marker-alt"></i> {{ event.venue }}</span>
          </div>
        </div>
        <div class="flex gap-3 flex-wrap items-center">
          <a
            v-if="publicLandingUrl"
            :href="publicLandingUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            <i class="fas fa-external-link-alt"></i> Public Landing
          </a>
          <router-link :to="`/events`" class="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition">
            <i class="fas fa-arrow-left"></i> Back
          </router-link>
          <router-link
            :to="`/events/${event.id}/checkin-search`"
            class="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-emerald-600 transition"
          >
            <i class="fas fa-search text-xs"></i> Search Check-in
          </router-link>
          <router-link
            to="/qr-checkin"
            class="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition"
          >
            <i class="fas fa-qrcode text-xs"></i> QR Scanner
          </router-link>
        </div>
      </header>

      <!-- Ticket Statistics -->
      <section v-if="stats" class="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <div class="rounded-2xl bg-primary-600 text-white p-5 shadow-sm">
          <p class="text-3xl font-semibold">{{ stats.totalTickets }}</p>
          <p class="text-sm text-white/80 mt-1">Total Tickets</p>
        </div>
        <div class="rounded-2xl bg-emerald-500 text-white p-5 shadow-sm">
          <p class="text-3xl font-semibold">{{ stats.totalSold || 0 }}</p>
          <p class="text-sm text-white/80 mt-1">Total Sold</p>
        </div>
        <div class="rounded-2xl bg-slate-500 text-white p-5 shadow-sm">
          <p class="text-3xl font-semibold">{{ stats.totalRemaining || 0 }}</p>
          <p class="text-sm text-white/80 mt-1">Total Remaining</p>
        </div>
        <div class="rounded-2xl bg-sky-500 text-white p-5 shadow-sm">
          <p class="text-3xl font-semibold">{{ stats.totalConfirmed || 0 }}</p>
          <p class="text-sm text-white/80 mt-1">Total Confirmed</p>
        </div>
        <div class="rounded-2xl bg-amber-500 text-white p-5 shadow-sm">
          <p class="text-3xl font-semibold">{{ stats.checkedInTickets || 0 }}</p>
          <p class="text-sm text-white/80 mt-1">Checked-In</p>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden">
        <div class="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h3 class="text-xl font-semibold text-slate-900">Ticket Groups</h3>
            <p class="text-sm text-slate-500">Manage group-specific checkout links shown on the public landing page.</p>
          </div>
        </div>

        <div v-if="ticketGroups.length === 0" class="px-6 py-8 text-sm text-slate-500">
          Create tickets or batches first to generate editable groups.
        </div>

        <div v-else class="divide-y divide-slate-100">
          <div
            v-for="group in ticketGroups"
            :key="group.id || group.groupKey"
            class="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between"
          >
            <div class="space-y-1">
              <div class="flex flex-wrap items-center gap-3">
                <h4 class="text-base font-semibold text-slate-900">{{ group.description }}</h4>
                <span
                  class="rounded-full px-2.5 py-1 text-xs font-semibold"
                  :class="group.active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'"
                >
                  {{ group.active !== false ? 'Active' : 'Inactive' }}
                </span>
                <span v-if="group.tables?.length" class="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  Tables {{ group.tables.join(', ') }}
                </span>
              </div>
              <p class="text-sm text-slate-500">
                {{ group.ticketCount }} ticket(s), {{ group.availableCount }} available
              </p>
              <p v-if="group.salesDescription" class="max-w-2xl text-sm text-slate-600">
                {{ group.salesDescription }}
              </p>
              <p class="text-xs text-slate-500">
                Current price: {{ formatCurrency(group.activePrice ?? group.price) }}
                <span v-if="group.activePricingTier"> • Active tier: {{ group.activePricingTier.name }}</span>
              </p>
              <p class="text-xs text-slate-500 break-all" v-if="event?.saleMode === 'checkout'">
                {{ group.checkoutUrl || 'Using event checkout URL fallback' }}
              </p>
              <p class="text-xs text-slate-500 break-all" v-else>
                Product ID: {{ group.productId || 'Not configured' }}
              </p>
            </div>
            <button
              class="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
              @click="editGroup(group)"
            >
              <i class="fas fa-link text-xs"></i> Edit Group
            </button>
          </div>
        </div>
      </section>

      <!-- Ticket Management -->
      <section class="rounded-3xl border border-slate-100 bg-white shadow-sm overflow-hidden">
        <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div class="flex flex-col sm:flex-row sm:items-center gap-4 flex-1">
            <h3 class="text-xl font-semibold text-slate-900">Tickets</h3>
            <div class="relative flex-1 max-w-sm">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                <i class="fas fa-search text-xs"></i>
              </span>
              <input 
                type="text" 
                class="w-full rounded-full border border-slate-200 pl-10 pr-10 py-2 text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white"
                placeholder="Search tickets..."
                v-model="searchValue"
              >
              <button 
                v-if="searchValue" 
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                @click="searchValue = ''"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
          <div class="flex gap-3 flex-wrap">
            <button class="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-emerald-600 transition" @click="showCreateModal">
              <i class="fas fa-plus text-xs"></i> Add Ticket
            </button>
            <button class="inline-flex items-center gap-2 rounded-full bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-sky-600 transition" @click="showBatchCreateModal">
              <i class="fas fa-layer-group text-xs"></i> Batch Create
            </button>
            <button
              class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm hover:bg-slate-50 transition disabled:opacity-60"
              @click="exportTicketsCsv"
              :disabled="isLoadingTickets || sortedTickets.length === 0"
              title="Export tickets to CSV"
            >
              <i class="fas fa-file-csv text-xs"></i> Export CSV
            </button>
          </div>
        </div>

        <!-- Bulk Actions Toolbar -->
        <div v-if="selectedTicketIds.length > 0" class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 px-6 py-3 bg-sky-50 border-b border-sky-100">
          <span class="text-sm text-sky-700 font-medium">
            <strong>{{ selectedTicketIds.length }}</strong> ticket(s) selected
          </span>
          <div class="flex gap-2 flex-wrap">
            <button class="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 transition shadow-sm" @click="showBulkEditModal">
              <i class="fas fa-edit"></i> Edit
            </button>
            <button
              class="inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-sky-700 transition shadow-sm disabled:opacity-60"
              @click="resendSelectedEmails"
              :disabled="isResending"
              title="Resend ticket emails for selected tickets"
            >
              <i class="fas fa-envelope"></i> {{ isResending ? 'Sending...' : 'Resend Emails' }}
            </button>
            <button class="inline-flex items-center gap-1.5 rounded-full bg-rose-500 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-rose-600 transition shadow-sm" @click="confirmBulkDelete">
              <i class="fas fa-trash"></i> Delete
            </button>
            <button class="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition" @click="clearSelection">
              <i class="fas fa-times"></i> Clear
            </button>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full min-w-[900px] table-fixed caption-bottom text-left text-sm text-slate-600">
            <thead class="border-b bg-slate-50 text-slate-500 uppercase text-[0.65rem] tracking-[0.4em]">
              <tr>
                <th class="px-5 py-3 w-10 text-center">
                  <input 
                    type="checkbox" 
                    :checked="isAllSelected" 
                    @change="toggleSelectAll"
                    class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  >
                </th>
                <th class="w-24 px-4 py-3 font-semibold whitespace-nowrap">
                  <div class="flex items-center justify-center gap-3 tracking-normal">
                    <button
                      type="button"
                      class="text-slate-500 transition hover:text-slate-900"
                      @click="sortBy('checkedIn')"
                      title="Sort by check-in status"
                    >
                      <span class="sr-only">Sort by check-in status</span>
                      <i class="fas fa-check-circle"></i>
                      <i v-if="sortKey === 'checkedIn'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                    </button>
                    <button
                      type="button"
                      class="text-slate-500 transition hover:text-slate-900"
                      @click="sortBy('accessoryCollected')"
                      title="Sort by kit status"
                    >
                      <span class="sr-only">Sort by kit status</span>
                      <i class="fas fa-box"></i>
                      <i v-if="sortKey === 'accessoryCollected'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                    </button>
                  </div>
                </th>
                <th @click="sortBy('identificationNumber')" class="w-24 px-5 py-3 font-semibold cursor-pointer hover:text-slate-900 transition whitespace-nowrap">
                  # <i v-if="sortKey === 'identificationNumber'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                </th>
                <th @click="sortBy('description')" class="px-5 py-3 font-semibold cursor-pointer hover:text-slate-900 transition whitespace-nowrap">
                  Description <i v-if="sortKey === 'description'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                </th>
                <th @click="sortBy('table')" class="w-24 px-5 py-3 font-semibold cursor-pointer hover:text-slate-900 transition whitespace-nowrap">
                  Table <i v-if="sortKey === 'table'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                </th>
                <th @click="sortBy('order')" class="w-36 px-5 py-3 font-semibold cursor-pointer hover:text-slate-900 transition whitespace-nowrap">
                  Order <i v-if="sortKey === 'order'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                </th>
                <th @click="sortBy('buyer')" class="w-56 px-5 py-3 font-semibold cursor-pointer hover:text-slate-900 transition whitespace-nowrap">
                  Buyer <i v-if="sortKey === 'buyer'" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down', 'ml-1']"></i>
                </th>
                <th class="w-32 px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr v-if="isLoadingTickets">
                <td colspan="8" class="px-5 py-12 text-center text-slate-500">
                  <div class="inline-flex items-center gap-2">
                    <span class="w-4 h-4 rounded-full border-2 border-slate-500 border-t-transparent animate-spin"></span>
                    Loading tickets...
                  </div>
                </td>
              </tr>
              <tr v-else-if="sortedTickets.length === 0">
                <td colspan="8" class="px-5 py-12 text-center text-slate-500">
                  {{ tickets.length === 0 ? 'No tickets yet. Create your first ticket for this event!' : 'No tickets found matching your search.' }}
                </td>
              </tr>
              <tr 
                v-else 
                v-for="ticket in sortedTickets" 
                :key="ticket.id"
                class="hover:bg-slate-50/50 transition duration-150"
              >
                <td class="px-5 py-4 text-center">
                  <input 
                    type="checkbox" 
                    :checked="selectedTicketIds.includes(ticket.id)"
                    @change="toggleTicketSelection(ticket.id)"
                    class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  >
                </td>
                <td class="px-4 py-4">
                  <div class="flex items-center justify-center gap-3">
                    <span v-if="ticket.checkedIn" class="text-emerald-500" title="Checked In">
                      <i class="fas fa-check-circle text-lg"></i>
                    </span>
                    <span v-else class="text-slate-200" title="Not Checked In">
                      <i class="far fa-circle text-lg"></i>
                    </span>
                    <span v-if="ticket.accessoryCollected" class="text-sky-500" title="Kit Picked Up">
                      <i class="fas fa-box text-lg"></i>
                    </span>
                    <span v-else class="text-slate-200" title="Kit Not Picked Up">
                      <i class="fas fa-box text-lg opacity-30"></i>
                    </span>
                  </div>
                </td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                    #{{ ticket.identificationNumber }}
                  </span>
                </td>
                <td class="px-5 py-4">
                  <p class="font-semibold text-slate-900">{{ ticket.description }}</p>
                  <p class="text-xs text-slate-500 mt-0.5">
                    ID: {{ ticket.id }}<span v-if="ticket.location"> • {{ ticket.location }}</span>
                  </p>
                </td>
                <td class="px-5 py-4 text-slate-600">
                  {{ ticket.table || '-' }}
                </td>
                <td class="px-5 py-4">
                  <button
                    v-if="ticket.order"
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 border border-sky-100 hover:bg-sky-100 transition shadow-sm"
                    title="Open confirmation page"
                    @click="openConfirmationPage(ticket.order)"
                  >
                    <i class="fas fa-external-link-alt text-[10px]"></i>
                    {{ ticket.order }}
                  </button>
                  <span v-else class="text-slate-400">-</span>
                </td>
                <td class="min-w-0 px-5 py-4">
                  <p class="truncate font-semibold text-slate-900" :title="ticket.buyer || ''">{{ ticket.buyer || '-' }}</p>
                  <p class="truncate text-xs text-slate-500" :title="ticket.buyerEmail || ''">{{ ticket.buyerEmail || '' }}</p>
                  <p class="truncate text-xs text-slate-500" :title="ticket.buyerPhone || ''">{{ ticket.buyerPhone || '' }}</p>
                </td>
                <td class="px-5 py-4">
                  <div class="flex justify-end gap-1.5">
                    <button 
                      class="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-primary-600 hover:border-primary-200 hover:bg-primary-50 transition shadow-sm" 
                      @click="editTicket(ticket)" 
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      v-if="canShowEmailAction(ticket)" 
                      class="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-sky-600 hover:border-sky-200 hover:bg-sky-50 transition shadow-sm"
                      @click="sendTicketEmailAction(ticket)" 
                      :title="getEmailActionTitle(ticket)"
                      :disabled="isResending"
                    >
                      <i class="fas fa-envelope"></i>
                    </button>
                    <button
                      v-if="isSoldTicket(ticket)"
                      class="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 transition shadow-sm"
                      @click="printTicket(ticket)"
                      title="View printable ticket"
                    >
                      <i class="fas fa-print"></i>
                    </button>
                    <button 
                      class="p-2 rounded-lg border border-slate-200 bg-white text-slate-400 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition shadow-sm" 
                      @click="deleteTicket(ticket.id)" 
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

        <!-- Error Messages -->
        <div v-if="error" class="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-4 text-sm text-rose-700 flex items-center justify-between gap-3">
          <div>
            <strong>Error:</strong> {{ error }}
          </div>
          <button class="text-xs font-semibold text-rose-600 underline" @click="loadTickets">Retry</button>
        </div>
    </div>

    <div v-if="isGroupModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" @click.self="isGroupModalOpen = false">
      <div class="w-full max-w-3xl rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60">
          <h5 class="text-lg font-semibold text-slate-900">Edit Group</h5>
          <button class="text-slate-400 hover:text-slate-600 transition p-2 rounded-full hover:bg-slate-100" @click="isGroupModalOpen = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="max-h-[75vh] space-y-5 overflow-y-auto p-6">
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Group name</p>
              <p class="mt-1 text-sm font-semibold text-slate-900">{{ groupForm.description || '-' }}</p>
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Tables</p>
              <p class="mt-1 text-sm font-semibold text-slate-900">{{ groupForm.tables || '-' }}</p>
            </div>
          </div>
          <label class="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <span>
              <span class="block text-sm font-semibold text-slate-700">Active on public page</span>
              <span class="mt-1 block text-xs text-slate-500">Inactive groups are hidden from the public event landing page.</span>
            </span>
            <input
              v-model="groupForm.active"
              type="checkbox"
              class="h-5 w-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            >
          </label>
          <div>
            <label for="groupSalesDescription" class="block text-sm font-semibold text-slate-700 mb-2">Buyer description</label>
            <textarea
              id="groupSalesDescription"
              v-model="groupForm.salesDescription"
              rows="3"
              maxlength="500"
              class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
              placeholder="Explain what this ticket group includes and how it works."
            ></textarea>
            <div class="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
              <p>Shown below the group name on the public sales page.</p>
              <span>{{ groupForm.salesDescription.length }}/500</span>
            </div>
          </div>
          <div v-if="event?.saleMode === 'checkout'">
            <label for="groupCheckoutUrl" class="block text-sm font-semibold text-slate-700 mb-2">Group checkout URL</label>
            <input
              id="groupCheckoutUrl"
              v-model="groupForm.checkoutUrl"
              type="url"
              class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
              placeholder="https://..."
            >
            <p class="mt-2 text-xs text-slate-500">Leave empty to use the event checkout URL on the public landing page.</p>
            <p v-if="groupForm.checkoutUrl && !isValidCheckoutUrl" class="mt-2 text-xs font-semibold text-red-600">Enter a valid HTTP or HTTPS URL.</p>
          </div>
          <div v-else>
            <label for="groupProductId" class="block text-sm font-semibold text-slate-700 mb-2">Product ID *</label>
            <input
              id="groupProductId"
              v-model.number="groupForm.productId"
              type="number"
              min="1"
              required
              :aria-invalid="!isValidProductId"
              class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
              placeholder="45"
            >
            <p class="mt-2 text-xs text-slate-500">Nova.Money product ID used in the shopping cart payload for this group.</p>
            <p v-if="!isValidProductId" class="mt-2 text-xs font-semibold text-red-600">Enter a positive whole-number product ID.</p>
          </div>
          <div>
            <label for="groupColor" class="block text-sm font-semibold text-slate-700 mb-2">Group Color</label>
            <div class="flex items-center gap-3">
              <input
                id="groupColor"
                v-model="groupForm.color"
                type="color"
                class="h-11 w-16 rounded-xl border border-slate-200 bg-slate-50 p-1"
              >
              <input
                v-model="groupForm.color"
                type="text"
                required
                :aria-invalid="!isValidGroupColor"
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
                placeholder="#EAB308"
              >
            </div>
            <p class="mt-2 text-xs text-slate-500">Shown as the color bar on the public landing page for this ticket group.</p>
            <p v-if="!isValidGroupColor" class="mt-2 text-xs font-semibold text-red-600">Use a six-digit hexadecimal color such as #EAB308.</p>
          </div>
          <div class="space-y-4 border-t border-slate-100 pt-5">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h6 class="text-sm font-semibold text-slate-900">Sales Batches</h6>
                <p class="mt-1 text-xs text-slate-500">Use either sales periods or sales quantities. Quantity batches are applied in the order registered.</p>
              </div>
              <div class="flex items-center gap-2">
                <select
                  v-model="groupForm.pricingType"
                  class="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
                  @change="applyPricingType"
                >
                  <option value="period">By sales period</option>
                  <option value="quantity">By sales quantity</option>
                </select>
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  @click="addPricingTier"
                >
                  <i class="fas fa-plus text-[0.65rem]"></i> Add Batch
                </button>
              </div>
            </div>

            <div v-if="groupForm.pricingTiers.length === 0" class="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
              No sales batches configured.
            </div>

            <p
              v-if="groupForm.pricingType === 'quantity' && groupForm.pricingTiers.length"
              class="text-xs font-semibold"
              :class="batchQuantityIsValid ? 'text-slate-500' : 'text-red-600'"
            >
              Configured {{ configuredBatchQuantity }} of {{ groupForm.ticketCount }} ticket(s).
              <span v-if="!batchQuantitiesArePositiveIntegers">Every batch must contain a whole number of at least 1 ticket.</span>
              <span v-else-if="batchQuantityExceedsInventory">Reduce the batch quantities to fit this ticket group.</span>
            </p>

            <div v-if="groupForm.pricingTiers.length" class="space-y-4">
              <div
                v-for="(tier, index) in groupForm.pricingTiers"
                :key="`tier-${index}`"
                class="rounded-2xl border border-slate-200 bg-slate-50 p-4"
              >
                <div class="flex items-center justify-between gap-3">
                  <p class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Batch {{ index + 1 }}</p>
                  <button
                    type="button"
                    class="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:bg-white"
                    @click="removePricingTier(index)"
                  >
                    Remove
                  </button>
                </div>
                <div class="mt-4 grid gap-4 md:grid-cols-2">
                  <div class="md:col-span-2">
                    <label class="mb-2 block text-sm font-semibold text-slate-700">Batch Name *</label>
                    <input
                      v-model="tier.name"
                      type="text"
                      required
                      :aria-invalid="!String(tier.name || '').trim()"
                      class="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
                      placeholder="Early Bird"
                    >
                    <p v-if="!String(tier.name || '').trim()" class="mt-2 text-xs font-semibold text-red-600">Batch name is required.</p>
                  </div>
                  <div v-if="tier.type === 'period'">
                    <label class="mb-2 block text-sm font-semibold text-slate-700">Start Datetime *</label>
                    <input
                      v-model="tier.startDateTime"
                      type="datetime-local"
                      required
                      class="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
                    >
                    <p v-if="!tier.startDateTime" class="mt-2 text-xs font-semibold text-red-600">Start datetime is required.</p>
                  </div>
                  <div v-if="tier.type === 'period'">
                    <label class="mb-2 block text-sm font-semibold text-slate-700">End Datetime *</label>
                    <input
                      v-model="tier.endDateTime"
                      type="datetime-local"
                      required
                      class="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
                    >
                    <p v-if="!tier.endDateTime" class="mt-2 text-xs font-semibold text-red-600">End datetime is required.</p>
                    <p v-else-if="tier.startDateTime && !periodBatchEndsAfterStart(tier)" class="mt-2 text-xs font-semibold text-red-600">End datetime must be after the start.</p>
                  </div>
                  <div v-else>
                    <label class="mb-2 block text-sm font-semibold text-slate-700">Tickets in Batch *</label>
                    <input
                      v-model.number="tier.quantity"
                      type="number"
                      min="1"
                      :max="groupForm.ticketCount"
                      step="1"
                      required
                      :aria-invalid="!batchQuantitiesArePositiveIntegers || batchQuantityExceedsInventory"
                      class="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
                      placeholder="25"
                    >
                  </div>
                  <div>
                    <label class="mb-2 block text-sm font-semibold text-slate-700">Price *</label>
                    <input
                      v-model.number="tier.price"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      :aria-invalid="!batchPriceIsValid(tier)"
                      class="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500"
                      placeholder="50.00"
                    >
                    <p v-if="!batchPriceIsValid(tier)" class="mt-2 text-xs font-semibold text-red-600">Enter a price of zero or more.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="periodBatchesOverlap" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
            Sales periods cannot overlap. Adjust the start or end datetime of the affected batches.
          </div>
          <div v-if="groupValidationErrors.length" class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Correct {{ groupValidationErrors.length }} validation issue(s) before saving the ticket group.
          </div>
        </div>
        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <button class="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition" @click="isGroupModalOpen = false">Cancel</button>
          <button class="rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-primary-500 transition disabled:opacity-60" @click="saveGroup" :disabled="isLoading || !isGroupFormValid">
            {{ isLoading ? 'Saving...' : 'Save Group' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Ticket Modal -->
    <div v-if="isTicketModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" @click.self="isTicketModalOpen = false">
      <div class="w-full max-w-2xl rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60">
          <h5 class="text-lg font-semibold text-slate-900">{{ isEditingTicket ? 'Edit Ticket' : 'Create Ticket' }}</h5>
          <button class="text-slate-400 hover:text-slate-600 transition p-2 rounded-full hover:bg-slate-100" @click="isTicketModalOpen = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="p-6 max-h-[70vh] overflow-y-auto">
          <form class="space-y-4" @submit.prevent="saveTicket">
            <div>
              <label for="ticketDescription" class="block text-sm font-semibold text-slate-700 mb-2">Description *</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketDescription" 
                v-model="ticketForm.description" 
                required
              >
            </div>
            <div>
              <label for="ticketPrice" class="block text-sm font-semibold text-slate-700 mb-2">Price *</label>
              <div class="relative">
                <span class="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-500 text-sm">$</span>
                <input 
                  type="number" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 pl-8 pr-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="ticketPrice" 
                  v-model="ticketForm.price" 
                  step="0.01" 
                  min="0"
                  required
                >
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="ticketLocation" class="block text-sm font-semibold text-slate-700 mb-2">Location</label>
                <input 
                  type="text" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="ticketLocation" 
                  v-model="ticketForm.location"
                >
              </div>
              <div>
                <label for="ticketTable" class="block text-sm font-semibold text-slate-700 mb-2">Table</label>
                <input 
                  type="number" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="ticketTable" 
                  v-model="ticketForm.table" 
                  min="1"
                >
              </div>
            </div>
            <div>
              <label for="ticketBuyer" class="block text-sm font-semibold text-slate-700 mb-2">Buyer</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketBuyer" 
                v-model="ticketForm.buyer"
              >
            </div>
            <div>
              <label for="ticketBuyerDocument" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Document</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketBuyerDocument" 
                v-model="ticketForm.buyerDocument"
              >
            </div>
            <div>
              <label for="ticketBuyerEmail" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Email</label>
              <input 
                type="email" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketBuyerEmail" 
                v-model="ticketForm.buyerEmail"
              >
            </div>
            <div>
              <label for="ticketBuyerPhone" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Phone</label>
              <input 
                type="tel" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketBuyerPhone" 
                v-model="ticketForm.buyerPhone"
              >
            </div>
            <div>
              <label for="ticketOrder" class="block text-sm font-semibold text-slate-700 mb-2">Order</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketOrder" 
                v-model="ticketForm.order"
              >
            </div>
            <div>
              <label for="ticketSalesEndDateTime" class="block text-sm font-semibold text-slate-700 mb-2">Sales End Date & Time</label>
              <input 
                type="datetime-local" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="ticketSalesEndDateTime" 
                v-model="ticketForm.salesEndDateTime"
              >
              <p class="mt-2 text-xs text-slate-500">Optional: Set when ticket sales should end</p>
            </div>
            
            <!-- Check-in Status Section -->
            <div v-if="isEditingTicket" class="space-y-4 pt-4 border-t border-slate-100">
              <h6 class="text-sm font-bold text-slate-900 uppercase tracking-wider">Check-in Status</h6>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div class="flex items-center gap-3 h-[42px]">
                  <input 
                    type="checkbox" 
                    class="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                    id="ticketCheckedIn" 
                    v-model="ticketForm.checkedIn"
                    @change="onCheckedInChange"
                  >
                  <label class="text-sm font-semibold text-slate-700 flex items-center gap-2" for="ticketCheckedIn">
                    Checked In
                    <span v-if="ticketForm.checkedIn" class="text-emerald-500">
                      <i class="fas fa-check-circle"></i>
                    </span>
                  </label>
                </div>
                <div>
                  <label for="ticketCheckedInAt" class="block text-sm font-semibold text-slate-700 mb-2">Checked In At</label>
                  <input 
                    type="datetime-local" 
                    class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                    id="ticketCheckedInAt" 
                    v-model="ticketForm.checkedInAt"
                    :disabled="!ticketForm.checkedIn"
                  >
                </div>
              </div>

              <!-- Accessory Pickup Status -->
              <h6 class="text-sm font-bold text-slate-900 uppercase tracking-wider pt-2">Accessory Pickup (Kit)</h6>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div class="flex items-center gap-3 h-[42px]">
                  <input 
                    type="checkbox" 
                    class="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                    id="ticketAccessoryCollected" 
                    v-model="ticketForm.accessoryCollected"
                    @change="onAccessoryCollectedChange"
                  >
                  <label class="text-sm font-semibold text-slate-700 flex items-center gap-2" for="ticketAccessoryCollected">
                    Kit Picked Up
                    <span v-if="ticketForm.accessoryCollected" class="text-primary-500">
                      <i class="fas fa-box"></i>
                    </span>
                  </label>
                </div>
                <div>
                  <label for="ticketAccessoryCollectedAt" class="block text-sm font-semibold text-slate-700 mb-2">Picked Up At</label>
                  <input 
                    type="datetime-local" 
                    class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                    id="ticketAccessoryCollectedAt" 
                    v-model="ticketForm.accessoryCollectedAt"
                    :disabled="!ticketForm.accessoryCollected"
                  >
                </div>
              </div>
              <div>
                <label for="ticketAccessoryCollectedNotes" class="block text-sm font-semibold text-slate-700 mb-2">Pickup Notes</label>
                <textarea 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="ticketAccessoryCollectedNotes" 
                  v-model="ticketForm.accessoryCollectedNotes" 
                  rows="2"
                  placeholder="Enter any observations about the kit pickup..."
                ></textarea>
              </div>
            </div>
          </form>
        </div>
        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <button class="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition" @click="isTicketModalOpen = false">Cancel</button>
          <button class="rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-primary-500 transition disabled:opacity-60" @click="saveTicket" :disabled="isLoading">
            {{ isLoading ? 'Saving...' : 'Save Ticket' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Batch Create Modal -->
    <div v-if="isBatchModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" @click.self="isBatchModalOpen = false">
      <div class="w-full max-w-2xl rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60">
          <h5 class="text-lg font-semibold text-slate-900">Batch Create Tickets</h5>
          <button class="text-slate-400 hover:text-slate-600 transition p-2 rounded-full hover:bg-slate-100" @click="isBatchModalOpen = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="p-6 max-h-[70vh] overflow-y-auto">
          <form class="space-y-4" @submit.prevent="saveBatchTickets">
            <div>
              <label for="batchDescription" class="block text-sm font-semibold text-slate-700 mb-2">Description *</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchDescription" 
                v-model="batchForm.description" 
                required
              >
            </div>
            <div>
              <label for="batchPrice" class="block text-sm font-semibold text-slate-700 mb-2">Price *</label>
              <div class="relative">
                <span class="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-500 text-sm">$</span>
                <input 
                  type="number" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 pl-8 pr-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="batchPrice" 
                  v-model="batchForm.price" 
                  step="0.01" 
                  min="0"
                  required
                >
              </div>
            </div>
            <div>
              <label for="batchQuantity" class="block text-sm font-semibold text-slate-700 mb-2">Quantity *</label>
              <input 
                type="number" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchQuantity" 
                v-model="batchForm.quantity" 
                min="1" 
                max="100"
                required
              >
              <p class="mt-2 text-xs text-slate-500">Create between 1 and 100 tickets at once</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="batchLocation" class="block text-sm font-semibold text-slate-700 mb-2">Location</label>
                <input 
                  type="text" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="batchLocation" 
                  v-model="batchForm.location"
                >
              </div>
              <div>
                <label for="batchTable" class="block text-sm font-semibold text-slate-700 mb-2">Table</label>
                <input 
                  type="number" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="batchTable" 
                  v-model="batchForm.table" 
                  min="1"
                >
              </div>
            </div>
            <div>
              <label for="batchBuyer" class="block text-sm font-semibold text-slate-700 mb-2">Buyer</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchBuyer" 
                v-model="batchForm.buyer"
              >
            </div>
            <div>
              <label for="batchBuyerDocument" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Document</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchBuyerDocument" 
                v-model="batchForm.buyerDocument"
              >
            </div>
            <div>
              <label for="batchBuyerEmail" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Email</label>
              <input 
                type="email" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchBuyerEmail" 
                v-model="batchForm.buyerEmail"
              >
            </div>
            <div>
              <label for="batchBuyerPhone" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Phone</label>
              <input 
                type="tel" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchBuyerPhone" 
                v-model="batchForm.buyerPhone"
              >
            </div>
            <div>
              <label for="batchOrder" class="block text-sm font-semibold text-slate-700 mb-2">Order</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchOrder" 
                v-model="batchForm.order"
              >
            </div>
            <div>
              <label for="batchSalesEndDateTime" class="block text-sm font-semibold text-slate-700 mb-2">Sales End Date & Time</label>
              <input 
                type="datetime-local" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                id="batchSalesEndDateTime" 
                v-model="batchForm.salesEndDateTime"
              >
              <p class="mt-2 text-xs text-slate-500">Optional: Set when ticket sales should end (applies to all tickets)</p>
            </div>
          </form>
        </div>
        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <button class="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition" @click="isBatchModalOpen = false">Cancel</button>
          <button class="rounded-full bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-sky-600 transition disabled:opacity-60" @click="saveBatchTickets" :disabled="isLoading">
            {{ isLoading ? 'Creating...' : `Create ${batchForm.quantity || 0} Tickets` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk Edit Modal -->
    <div v-if="isBulkEditModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" @click.self="isBulkEditModalOpen = false">
      <div class="w-full max-w-2xl rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden">
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/60">
          <h5 class="text-lg font-semibold text-slate-900">Bulk Edit {{ selectedTicketIds.length }} Ticket(s)</h5>
          <button class="text-slate-400 hover:text-slate-600 transition p-2 rounded-full hover:bg-slate-100" @click="isBulkEditModalOpen = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="p-6 max-h-[70vh] overflow-y-auto">
          <div class="rounded-2xl border border-sky-100 bg-sky-50 px-4 py-3 text-sm text-sky-700 flex items-start gap-3 mb-6">
            <i class="fas fa-info-circle text-sky-500 text-lg mt-0.5"></i>
            <p>Fill in fields to update them, or check "Clear" to set them as empty.</p>
          </div>
          <form class="space-y-4" @submit.prevent="saveBulkEdit">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label for="bulkLocation" class="block text-sm font-semibold text-slate-700 mb-2">Location</label>
                <input 
                  type="text" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                  id="bulkLocation" 
                  v-model="bulkEditForm.location"
                  :disabled="bulkEditForm.clearLocation"
                  placeholder="Leave empty to skip"
                >
                <div class="flex items-center gap-2 mt-2">
                  <input 
                    type="checkbox" 
                    class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                    id="clearLocation" 
                    v-model="bulkEditForm.clearLocation"
                  >
                  <label class="text-xs text-slate-500 font-medium" for="clearLocation">
                    Clear this field
                  </label>
                </div>
              </div>
              <div>
                <label for="bulkTable" class="block text-sm font-semibold text-slate-700 mb-2">Table</label>
                <input 
                  type="number" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                  id="bulkTable" 
                  v-model="bulkEditForm.table" 
                  min="1"
                  :disabled="bulkEditForm.clearTable"
                  placeholder="Leave empty to skip"
                >
                <div class="flex items-center gap-2 mt-2">
                  <input 
                    type="checkbox" 
                    class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                    id="clearTable" 
                    v-model="bulkEditForm.clearTable"
                  >
                  <label class="text-xs text-slate-500 font-medium" for="clearTable">
                    Clear this field
                  </label>
                </div>
              </div>
            </div>
            <div>
              <label for="bulkOrder" class="block text-sm font-semibold text-slate-700 mb-2">Order</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                id="bulkOrder" 
                v-model="bulkEditForm.order"
                :disabled="bulkEditForm.clearOrder"
                placeholder="Leave empty to skip"
              >
              <div class="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="clearOrder" 
                  v-model="bulkEditForm.clearOrder"
                >
                <label class="text-xs text-slate-500 font-medium" for="clearOrder">
                  Clear this field
                </label>
              </div>
            </div>
            <div>
              <label for="bulkBuyer" class="block text-sm font-semibold text-slate-700 mb-2">Buyer</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                id="bulkBuyer" 
                v-model="bulkEditForm.buyer"
                :disabled="bulkEditForm.clearBuyer"
                placeholder="Leave empty to skip"
              >
              <div class="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="clearBuyer" 
                  v-model="bulkEditForm.clearBuyer"
                >
                <label class="text-xs text-slate-500 font-medium" for="clearBuyer">
                  Clear this field
                </label>
              </div>
            </div>
            <div>
              <label for="bulkBuyerDocument" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Document</label>
              <input 
                type="text" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                id="bulkBuyerDocument" 
                v-model="bulkEditForm.buyerDocument"
                :disabled="bulkEditForm.clearBuyerDocument"
                placeholder="Leave empty to skip"
              >
              <div class="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="clearBuyerDocument" 
                  v-model="bulkEditForm.clearBuyerDocument"
                >
                <label class="text-xs text-slate-500 font-medium" for="clearBuyerDocument">
                  Clear this field
                </label>
              </div>
            </div>
            <div>
              <label for="bulkBuyerEmail" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Email</label>
              <input 
                type="email" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                id="bulkBuyerEmail" 
                v-model="bulkEditForm.buyerEmail"
                :disabled="bulkEditForm.clearBuyerEmail"
                placeholder="Leave empty to skip"
              >
              <div class="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="clearBuyerEmail" 
                  v-model="bulkEditForm.clearBuyerEmail"
                >
                <label class="text-xs text-slate-500 font-medium" for="clearBuyerEmail">
                  Clear this field
                </label>
              </div>
            </div>
            <div>
              <label for="bulkBuyerPhone" class="block text-sm font-semibold text-slate-700 mb-2">Buyer Phone</label>
              <input 
                type="tel" 
                class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500 disabled:opacity-50" 
                id="bulkBuyerPhone" 
                v-model="bulkEditForm.buyerPhone"
                :disabled="bulkEditForm.clearBuyerPhone"
                placeholder="Leave empty to skip"
              >
              <div class="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  class="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="clearBuyerPhone" 
                  v-model="bulkEditForm.clearBuyerPhone"
                >
                <label class="text-xs text-slate-500 font-medium" for="clearBuyerPhone">
                  Clear this field
                </label>
              </div>
            </div>
            
            <div class="space-y-4 pt-4 border-t border-slate-100">
              <h6 class="text-sm font-bold text-slate-900 uppercase tracking-wider">Check-in Status</h6>
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  class="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="bulkCheckedIn" 
                  v-model="bulkEditForm.checkedIn"
                >
                <label class="text-sm font-semibold text-slate-700" for="bulkCheckedIn">
                  Mark as Checked In
                </label>
              </div>
              <div v-if="bulkEditForm.checkedIn">
                <label for="bulkCheckedInAt" class="block text-sm font-semibold text-slate-700 mb-2">Checked In At</label>
                <input 
                  type="datetime-local" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="bulkCheckedInAt" 
                  v-model="bulkEditForm.checkedInAt"
                >
                <p class="mt-2 text-xs text-slate-500">Leave empty to use current date/time</p>
              </div>
            </div>

            <div class="space-y-4 pt-4 border-t border-slate-100">
              <h6 class="text-sm font-bold text-slate-900 uppercase tracking-wider">Accessory Pickup Status (Kit)</h6>
              <div class="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  class="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500" 
                  id="bulkAccessoryCollected" 
                  v-model="bulkEditForm.accessoryCollected"
                >
                <label class="text-sm font-semibold text-slate-700" for="bulkAccessoryCollected">
                  Mark as Kit Picked Up
                </label>
              </div>
              <div v-if="bulkEditForm.accessoryCollected">
                <label for="bulkAccessoryCollectedAt" class="block text-sm font-semibold text-slate-700 mb-2">Picked Up At</label>
                <input 
                  type="datetime-local" 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="bulkAccessoryCollectedAt" 
                  v-model="bulkEditForm.accessoryCollectedAt"
                >
                <p class="mt-2 text-xs text-slate-500">Leave empty to use current date/time</p>
              </div>
              <div>
                <label for="bulkAccessoryCollectedNotes" class="block text-sm font-semibold text-slate-700 mb-2">Pickup Notes</label>
                <textarea 
                  class="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700 shadow-sm transition focus:border-primary-500 focus:ring-primary-500" 
                  id="bulkAccessoryCollectedNotes" 
                  v-model="bulkEditForm.accessoryCollectedNotes" 
                  rows="2"
                  placeholder="Leave empty to skip"
                ></textarea>
              </div>
            </div>
          </form>
        </div>
        <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/60">
          <button class="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 transition" @click="isBulkEditModalOpen = false">Cancel</button>
          <button class="rounded-full bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-primary-500 transition disabled:opacity-60" @click="saveBulkEdit" :disabled="isLoading">
            {{ isLoading ? 'Updating...' : `Update ${selectedTicketIds.length} Ticket(s)` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useApi } from '@/composables/useApi'
import { useUser } from '@/composables/useUser'
import { formatDateTimeLocalInput, serializeDateTimeLocalInput } from '@/utils/dateTime'
// REMOVED: import EasyDataTable

// Props and route
const route = useRoute()
const eventId = computed(() => route.params.id)
const publicLandingUrl = computed(() => (event.value?.publicHash ? `/event/${event.value.publicHash}` : ''))

// Composables
const { isLoading, error, get, post, put, delete: deleteApi } = useApi()
const { isAuthenticated } = useUser()

// Data
const event = ref(null)
const tickets = ref([])
const stats = ref(null)
const isLoadingEvent = ref(false)
const isLoadingTickets = ref(false)
const isEditingTicket = ref(false)
const currentTicketId = ref(null)
const isResending = ref(false)
const searchValue = ref('')
const selectedTicketIds = ref([])
const ticketGroups = ref([])
const currentGroupId = ref(null)

// Sorting state
const sortKey = ref('identificationNumber')
const sortOrder = ref('asc')

// Filtering and Sorting Logic
const filteredTickets = computed(() => {
  if (!searchValue.value) return tickets.value
  
  const search = searchValue.value.toLowerCase()
  const searchDigits = searchValue.value.replace(/\D/g, '')
  return tickets.value.filter(ticket => {
    const buyerDocument = ticket.buyerDocument || ''
    return (
      ticket.description?.toLowerCase().includes(search) ||
      ticket.location?.toLowerCase().includes(search) ||
      ticket.buyer?.toLowerCase().includes(search) ||
      buyerDocument.toLowerCase().includes(search) ||
      (searchDigits && buyerDocument.replace(/\D/g, '').includes(searchDigits)) ||
      ticket.buyerEmail?.toLowerCase().includes(search) ||
      ticket.buyerPhone?.toLowerCase().includes(search) ||
      ticket.order?.toLowerCase().includes(search) ||
      ticket.id?.toString().includes(search) ||
      ticket.identificationNumber?.toString().includes(search)
    )
  })
})

const sortedTickets = computed(() => {
  const temp = [...filteredTickets.value]
  temp.sort((a, b) => {
    let valA = a[sortKey.value]
    let valB = b[sortKey.value]
    
    if (valA === null || valA === undefined) valA = ''
    if (valB === null || valB === undefined) valB = ''
    
    if (typeof valA === 'string') valA = valA.toLowerCase()
    if (typeof valB === 'string') valB = valB.toLowerCase()
    
    if (valA < valB) return sortOrder.value === 'asc' ? -1 : 1
    if (valA > valB) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
  return temp
})

const isTicketBuyerConfirmed = (ticket) => {
  return Boolean(ticket?.buyer && ticket?.buyerDocument && ticket?.buyerEmail)
}

const getOrderTickets = (orderId, eventId) => {
  if (!orderId) return []
  return tickets.value.filter((ticket) => ticket.order === orderId && ticket.eventId === eventId)
}

const isOrderConfirmed = (orderId, eventId) => {
  const orderTickets = getOrderTickets(orderId, eventId)
  return orderTickets.length > 0 && orderTickets.every(isTicketBuyerConfirmed)
}

const canSendConfirmationEmail = (ticket) => {
  return Boolean(ticket?.order && ticket?.buyerEmail && !isOrderConfirmed(ticket.order, ticket.eventId))
}

const canResendTicketEmail = (ticket) => {
  return isTicketBuyerConfirmed(ticket)
}

const isSoldTicket = (ticket) => {
  return Boolean(ticket?.order)
}

const canShowEmailAction = (ticket) => {
  return canSendConfirmationEmail(ticket) || canResendTicketEmail(ticket)
}

const getEmailActionTitle = (ticket) => {
  if (canSendConfirmationEmail(ticket)) {
    return 'Resend confirmation email'
  }

  return 'Resend ticket email'
}

const sortBy = (key) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return ''
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value))
}

// Select All Logic
const isAllSelected = computed(() => {
  return sortedTickets.value.length > 0 && selectedTicketIds.value.length === sortedTickets.value.length
})

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedTicketIds.value = []
  } else {
    selectedTicketIds.value = sortedTickets.value.map(t => t.id)
  }
}

// Modal States
const isTicketModalOpen = ref(false)
const isBatchModalOpen = ref(false)
const isBulkEditModalOpen = ref(false)
const isGroupModalOpen = ref(false)

// Forms
const ticketForm = reactive({
  description: '',
  location: '',
  table: null,
  price: 0,
  buyer: '',
  buyerDocument: '',
  buyerEmail: '',
  buyerPhone: '',
  order: '',
  salesEndDateTime: '',
  checkedIn: false,
  checkedInAt: '',
  accessoryCollected: false,
  accessoryCollectedAt: '',
  accessoryCollectedNotes: ''
})

const batchForm = reactive({
  description: '',
  location: '',
  table: null,
  price: 0,
  quantity: 1,
  buyer: '',
  buyerDocument: '',
  buyerEmail: '',
  buyerPhone: '',
  order: '',
  salesEndDateTime: ''
})

const bulkEditForm = reactive({
  location: '',
  table: '',
  order: '',
  buyer: '',
  buyerDocument: '',
  buyerEmail: '',
  buyerPhone: '',
  checkedIn: false,
  checkedInAt: '',
  accessoryCollected: false,
  accessoryCollectedAt: '',
  accessoryCollectedNotes: '',
  clearLocation: false,
  clearTable: false,
  clearOrder: false,
  clearBuyer: false,
  clearBuyerDocument: false,
  clearBuyerEmail: false,
  clearBuyerPhone: false
})

const groupForm = reactive({
  description: '',
  salesDescription: '',
  tables: '',
  checkoutUrl: '',
  productId: null,
  color: '#94a3b8',
  active: true,
  ticketCount: 0,
  pricingType: 'period',
  pricingTiers: []
})

// Headers are now handled directly in the template

// REMOVED: Bootstrap modals

// Load event data
const loadEvent = async () => {
  try {
    isLoadingEvent.value = true
    const data = await get(`/api/events/${eventId.value}`)
    
    if (data.success && data.event) {
      event.value = data.event
    } else {
      event.value = null
    }
  } catch (err) {
    console.error('Failed to load event:', err)
    event.value = null
  } finally {
    isLoadingEvent.value = false
  }
}

// Load tickets
const loadTickets = async () => {
  try {
    isLoadingTickets.value = true
    const data = await get(`/api/events/${eventId.value}/tickets`)
    
    tickets.value = data.tickets || []
    error.value = null
  } catch (err) {
    console.error('Failed to load tickets:', err)
    tickets.value = []
    error.value = err.message || 'Failed to load tickets'
  } finally {
    isLoadingTickets.value = false
  }
}

// Load statistics
const loadStats = async () => {
  try {
    const data = await get(`/api/events/${eventId.value}/tickets/stats`)
    stats.value = data.stats || null
  } catch (err) {
    console.error('Failed to load stats:', err)
    stats.value = null
  }
}

const loadGroups = async () => {
  try {
    const data = await get(`/api/events/${eventId.value}/groups`)
    ticketGroups.value = data.groups || []
  } catch (err) {
    console.error('Failed to load ticket groups:', err)
    ticketGroups.value = []
  }
}

// Show create modal
const showCreateModal = () => {
  isEditingTicket.value = false
  currentTicketId.value = null
  resetTicketForm()
  isTicketModalOpen.value = true
}

// Show batch create modal
const showBatchCreateModal = () => {
  resetBatchForm()
  isBatchModalOpen.value = true
}

const toDateTimeLocalValue = (value) => {
  return formatDateTimeLocalInput(value)
}

const mapPricingTierForm = (tier = {}, defaultType = 'period') => ({
  name: tier.name || '',
  type: tier.type || defaultType,
  startDateTime: toDateTimeLocalValue(tier.startDateTime),
  endDateTime: toDateTimeLocalValue(tier.endDateTime),
  quantity: tier.quantity ?? 1,
  price: tier.price ?? 0
})

const addPricingTier = () => {
  groupForm.pricingTiers.push(mapPricingTierForm({}, groupForm.pricingType))
}

const applyPricingType = () => {
  groupForm.pricingTiers.forEach((tier) => {
    tier.type = groupForm.pricingType
  })
}

const removePricingTier = (index) => {
  groupForm.pricingTiers.splice(index, 1)
}

const configuredBatchQuantity = computed(() => groupForm.pricingTiers.reduce(
  (total, tier) => total + (Number.isFinite(Number(tier.quantity)) ? Number(tier.quantity) : 0),
  0
))

const batchQuantitiesArePositiveIntegers = computed(() => (
  groupForm.pricingType !== 'quantity' || groupForm.pricingTiers.every((tier) => (
    Number.isInteger(Number(tier.quantity)) && Number(tier.quantity) > 0
  ))
))

const batchQuantityExceedsInventory = computed(() => (
  groupForm.pricingType === 'quantity' && configuredBatchQuantity.value > groupForm.ticketCount
))

const batchQuantityIsValid = computed(() => (
  batchQuantitiesArePositiveIntegers.value && !batchQuantityExceedsInventory.value
))

const batchPriceIsValid = (tier) => {
  if (tier.price === '' || tier.price === null || tier.price === undefined) return false
  const price = Number(tier.price)
  return Number.isFinite(price) && price >= 0
}

const periodBatchEndsAfterStart = (tier) => {
  if (!tier.startDateTime || !tier.endDateTime) return false
  const start = new Date(tier.startDateTime).getTime()
  const end = new Date(tier.endDateTime).getTime()
  return Number.isFinite(start) && Number.isFinite(end) && end > start
}

const periodBatchesOverlap = computed(() => {
  if (groupForm.pricingType !== 'period') return false

  const periods = groupForm.pricingTiers
    .filter((tier) => periodBatchEndsAfterStart(tier))
    .map((tier) => ({
      start: new Date(tier.startDateTime).getTime(),
      end: new Date(tier.endDateTime).getTime()
    }))
    .sort((a, b) => a.start - b.start)

  return periods.some((period, index) => index > 0 && period.start < periods[index - 1].end)
})

const isValidCheckoutUrl = computed(() => {
  const value = String(groupForm.checkoutUrl || '').trim()
  if (!value) return true

  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
})

const isValidProductId = computed(() => {
  if (event.value?.saleMode === 'checkout') return true
  const productId = Number(groupForm.productId)
  return Number.isInteger(productId) && productId > 0
})

const isValidGroupColor = computed(() => /^#[0-9A-Fa-f]{6}$/.test(String(groupForm.color || '').trim()))

const groupValidationErrors = computed(() => {
  const validationErrors = []

  if (!String(groupForm.description || '').trim()) validationErrors.push('Group name is required')
  if (String(groupForm.salesDescription || '').length > 500) validationErrors.push('Buyer description is too long')
  if (!isValidCheckoutUrl.value) validationErrors.push('Checkout URL is invalid')
  if (!isValidProductId.value) validationErrors.push('Product ID is invalid')
  if (!isValidGroupColor.value) validationErrors.push('Group color is invalid')

  groupForm.pricingTiers.forEach((tier, index) => {
    const batchLabel = `Batch ${index + 1}`
    if (!String(tier.name || '').trim()) validationErrors.push(`${batchLabel} name is required`)
    if (!batchPriceIsValid(tier)) validationErrors.push(`${batchLabel} price is invalid`)

    if (groupForm.pricingType === 'period') {
      if (!tier.startDateTime) validationErrors.push(`${batchLabel} start datetime is required`)
      if (!tier.endDateTime) validationErrors.push(`${batchLabel} end datetime is required`)
      if (tier.startDateTime && tier.endDateTime && !periodBatchEndsAfterStart(tier)) {
        validationErrors.push(`${batchLabel} period is invalid`)
      }
    }
  })

  if (!batchQuantitiesArePositiveIntegers.value) validationErrors.push('Batch quantities are invalid')
  if (batchQuantityExceedsInventory.value) validationErrors.push('Batch quantities exceed ticket inventory')
  if (periodBatchesOverlap.value) validationErrors.push('Batch periods overlap')

  return validationErrors
})

const isGroupFormValid = computed(() => groupValidationErrors.value.length === 0)

const editGroup = (group) => {
  currentGroupId.value = group.id
  const pricingType = group.pricingTiers?.[0]?.type || 'period'
  Object.assign(groupForm, {
    description: group.description || '',
    salesDescription: group.salesDescription || '',
    tables: group.tables?.length ? group.tables.join(', ') : '',
    checkoutUrl: group.checkoutUrl || '',
    productId: group.productId || null,
    color: group.color || '#94a3b8',
    active: group.active !== false,
    ticketCount: group.ticketCount || 0,
    pricingType,
    pricingTiers: (group.pricingTiers || []).map((tier) => mapPricingTierForm(tier, pricingType))
  })
  isGroupModalOpen.value = true
}

const saveGroup = async () => {
  if (!currentGroupId.value) {
    error.value = 'Ticket group not found'
    return
  }

  if (!isGroupFormValid.value) {
    error.value = 'Correct the validation issues in the ticket group before saving'
    return
  }

  try {
    await put(`/api/events/${eventId.value}/groups/${currentGroupId.value}`, {
      salesDescription: groupForm.salesDescription.trim(),
      checkoutUrl: groupForm.checkoutUrl.trim(),
      productId: groupForm.productId,
      color: groupForm.color.trim(),
      active: groupForm.active,
      pricingTiers: groupForm.pricingTiers.map((tier) => ({
        name: tier.name.trim(),
        type: groupForm.pricingType,
        startDateTime: groupForm.pricingType === 'period' ? serializeDateTimeLocalInput(tier.startDateTime) : null,
        endDateTime: groupForm.pricingType === 'period' ? serializeDateTimeLocalInput(tier.endDateTime) : null,
        quantity: groupForm.pricingType === 'quantity' ? tier.quantity : null,
        price: tier.price
      }))
    })

    isGroupModalOpen.value = false
    currentGroupId.value = null
    Object.assign(groupForm, { description: '', salesDescription: '', tables: '', checkoutUrl: '', productId: null, color: '#94a3b8', active: true, ticketCount: 0, pricingType: 'period', pricingTiers: [] })
    await loadGroups()
    error.value = null
  } catch (err) {
    console.error('Failed to save ticket group:', err)
    error.value = err.message || 'Failed to save ticket group'
  }
}

const csvColumns = [
  { key: 'id', label: 'id' },
  { key: 'eventId', label: 'eventId' },
  { key: 'description', label: 'description' },
  { key: 'identificationNumber', label: 'identificationNumber' },
  { key: 'location', label: 'location' },
  { key: 'table', label: 'table' },
  { key: 'price', label: 'price' },
  { key: 'order', label: 'order' },
  { key: 'buyer', label: 'buyer' },
  { key: 'buyerDocument', label: 'buyerDocument' },
  { key: 'buyerEmail', label: 'buyerEmail' },
  { key: 'buyerPhone', label: 'buyerPhone' },
  { key: 'salesEndDateTime', label: 'salesEndDateTime' },
  { key: 'checkedIn', label: 'checkedIn' },
  { key: 'checkedInAt', label: 'checkedInAt' },
  { key: 'accessoryCollected', label: 'accessoryCollected' },
  { key: 'accessoryCollectedAt', label: 'accessoryCollectedAt' },
  { key: 'accessoryCollectedNotes', label: 'accessoryCollectedNotes' },
  { key: 'created_at', label: 'created_at' },
  { key: 'updated_at', label: 'updated_at' }
]

const normalizeCsvValue = (value) => {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString()
  return String(value)
}

const escapeCsvValue = (value) => {
  const stringValue = normalizeCsvValue(value)
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }
  return stringValue
}

const sanitizeFilename = (value) => {
  if (!value) return 'event'
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const exportTicketsCsv = () => {
  const rows = sortedTickets.value
  if (!rows.length) return

  const header = csvColumns.map(col => col.label).join(',')
  const lines = rows.map(row => {
    return csvColumns.map(col => escapeCsvValue(row[col.key])).join(',')
  })

  const csvContent = [header, ...lines].join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const eventLabel = event.value?.title || event.value?.name || `event-${eventId.value}`
  const safeName = sanitizeFilename(eventLabel)
  const dateStamp = new Date().toISOString().slice(0, 10)
  const filename = `${safeName}-tickets-${dateStamp}.csv`

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Edit ticket
const editTicket = (ticket) => {
  isEditingTicket.value = true
  currentTicketId.value = ticket.id
  
  // Populate form
  Object.assign(ticketForm, {
    description: ticket.description || '',
    location: ticket.location || '',
    table: ticket.table || null,
    price: ticket.price || 0,
    buyer: ticket.buyer || '',
    buyerDocument: ticket.buyerDocument || '',
    buyerEmail: ticket.buyerEmail || '',
    buyerPhone: ticket.buyerPhone || '',
    order: ticket.order || '',
    salesEndDateTime: formatDateTimeLocalInput(ticket.salesEndDateTime),
    checkedIn: ticket.checkedIn || false,
    checkedInAt: formatDateTimeLocalInput(ticket.checkedInAt),
    accessoryCollected: ticket.accessoryCollected || false,
    accessoryCollectedAt: formatDateTimeLocalInput(ticket.accessoryCollectedAt),
    accessoryCollectedNotes: ticket.accessoryCollectedNotes || ''
  })
  
  isTicketModalOpen.value = true
}

// Save ticket
const saveTicket = async () => {
  if (!ticketForm.description || !ticketForm.price) {
    error.value = 'Description and price are required'
    return
  }
  
  try {
    const payload = {
      ...ticketForm,
      salesEndDateTime: serializeDateTimeLocalInput(ticketForm.salesEndDateTime),
      checkedInAt: serializeDateTimeLocalInput(ticketForm.checkedInAt),
      accessoryCollectedAt: serializeDateTimeLocalInput(ticketForm.accessoryCollectedAt)
    }

    let result
    if (isEditingTicket.value) {
      result = await put(`/api/tickets/${currentTicketId.value}`, payload)
    } else {
      result = await post(`/api/events/${eventId.value}/tickets`, payload)
    }
    
    console.log('Ticket saved:', result)
    
    // Close modal and reload
    isTicketModalOpen.value = false
    resetTicketForm()
    await loadTickets()
    await loadGroups()
    await loadStats()
    
    error.value = null
  } catch (err) {
    console.error('Failed to save ticket:', err)
    error.value = err.message || 'Failed to save ticket'
  }
}

// Save batch tickets
const saveBatchTickets = async () => {
  if (!batchForm.description || !batchForm.price || !batchForm.quantity) {
    error.value = 'Description, price, and quantity are required'
    return
  }
  
  try {
    const result = await post(`/api/events/${eventId.value}/tickets/batch`, {
      ...batchForm,
      salesEndDateTime: serializeDateTimeLocalInput(batchForm.salesEndDateTime)
    })
    console.log('Batch tickets created:', result)
    
    // Close modal and reload
    isBatchModalOpen.value = false
    resetBatchForm()
    await loadTickets()
    await loadGroups()
    await loadStats()
    
    error.value = null
  } catch (err) {
    console.error('Failed to create batch tickets:', err)
    error.value = err.message || 'Failed to create tickets'
  }
}

// Resend email for a ticket
const resendEmail = async (ticket) => {
  if (!ticket.buyerEmail) {
    error.value = 'Ticket does not have a buyer email'
    return
  }
  
  if (!confirm(`Resend email to ${ticket.buyerEmail}?`)) {
    return
  }
  
  try {
    isResending.value = true
    const result = await post(`/api/tickets/${ticket.id}/resend-email`, {})
    
    if (result.success) {
      alert(`Email successfully sent to ${ticket.buyerEmail}!`)
    }
    
    error.value = null
  } catch (err) {
    console.error('Failed to resend email:', err)
    error.value = err.message || 'Failed to resend email'
    alert(`Failed to resend email: ${err.message}`)
  } finally {
    isResending.value = false
  }
}

const resendConfirmationEmail = async (ticket) => {
  if (!ticket.buyerEmail) {
    error.value = 'Ticket does not have a buyer email'
    return
  }

  if (!confirm(`Resend confirmation email to ${ticket.buyerEmail}?`)) {
    return
  }

  try {
    isResending.value = true
    const result = await post(`/api/tickets/${ticket.id}/resend-confirmation-email`, {})

    if (result.success) {
      alert(`Confirmation email successfully sent to ${ticket.buyerEmail}!`)
    }

    error.value = null
  } catch (err) {
    console.error('Failed to resend confirmation email:', err)
    error.value = err.message || 'Failed to resend confirmation email'
    alert(`Failed to resend confirmation email: ${err.message}`)
  } finally {
    isResending.value = false
  }
}

const sendTicketEmailAction = async (ticket) => {
  if (canSendConfirmationEmail(ticket)) {
    await resendConfirmationEmail(ticket)
    return
  }

  await resendEmail(ticket)
}

const printTicket = async (ticket) => {
  const printWindow = window.open('', '_blank')

  if (!printWindow) {
    error.value = 'Your browser blocked the print window. Please allow pop-ups and try again.'
    return
  }

  printWindow.document.write('<title>Preparing ticket...</title>')

  try {
    const result = await get(`/api/tickets/${ticket.id}/printable`)
    const previewControls = `
      <style>
        .ticket-preview-actions { position: sticky; top: 0; z-index: 10; display: flex; align-items: center; justify-content: center; gap: 10px; padding: 12px 16px; background: rgba(255, 255, 255, 0.96); border-bottom: 1px solid #e5e7eb; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); font-family: Arial, sans-serif; }
        .ticket-preview-actions button { border: 0; border-radius: 999px; padding: 10px 16px; color: #ffffff; background: #047857; font-size: 14px; font-weight: 700; cursor: pointer; }
        .ticket-preview-actions button:hover { background: #065f46; }
        .ticket-preview-actions .ticket-preview-download { background: #111827; }
        .ticket-preview-actions .ticket-preview-download:hover { background: #374151; }
        .ticket-preview-actions span { color: #64748b; font-size: 12px; }
        @media print { .ticket-preview-actions { display: none !important; } }
      </style>
      <div class="ticket-preview-actions" role="toolbar" aria-label="Ticket actions">
        <button type="button" onclick="window.print()">Print</button>
        <button type="button" class="ticket-preview-download" onclick="window.print()">Download PDF</button>
        <span>Choose “Save as PDF” in the print dialog.</span>
      </div>`
    const previewHtml = result.html.replace('<body>', `<body>${previewControls}`)

    printWindow.document.open()
    printWindow.document.write(previewHtml)
    printWindow.document.close()
    printWindow.focus()
    error.value = null
  } catch (err) {
    printWindow.close()
    console.error('Failed to prepare printable ticket:', err)
    error.value = err.message || 'Failed to prepare printable ticket'
  }
}

// Resend email for selected tickets
const resendSelectedEmails = async () => {
  const count = selectedTicketIds.value.length

  if (count === 0) {
    return
  }

  if (!confirm(`Resend ticket emails for ${count} selected ticket(s)? Tickets without buyer name or email will be skipped.`)) {
    return
  }

  try {
    isResending.value = true
    const result = await post('/api/tickets/bulk-resend-email', {
      ticketIds: selectedTicketIds.value
    })

    if (result.success) {
      const skippedMessage = result.totalSkipped ? ` ${result.totalSkipped} skipped.` : ''
      const failedMessage = result.totalFailed ? ` ${result.totalFailed} failed.` : ''
      alert(`${result.totalSent} email(s) sent.${skippedMessage}${failedMessage}`)
    }

    error.value = result.totalFailed ? 'Some ticket emails could not be resent' : null
  } catch (err) {
    console.error('Failed to resend selected emails:', err)
    error.value = err.message || 'Failed to resend selected emails'
    alert(`Failed to resend selected emails: ${err.message}`)
  } finally {
    isResending.value = false
  }
}

// Delete ticket
const deleteTicket = async (ticketId) => {
  if (!confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
    return
  }
  
  try {
    await deleteApi(`/api/tickets/${ticketId}`)
    await loadTickets()
    await loadGroups()
    await loadStats()
    error.value = null
  } catch (err) {
    console.error('Failed to delete ticket:', err)
    error.value = err.message || 'Failed to delete ticket'
  }
}

// Reset forms
const resetTicketForm = () => {
  Object.assign(ticketForm, {
    description: '',
    location: '',
    table: null,
    price: 0,
    buyer: '',
    buyerDocument: '',
    buyerEmail: '',
    buyerPhone: '',
    order: '',
    salesEndDateTime: '',
    checkedIn: false,
    checkedInAt: '',
    accessoryCollected: false,
    accessoryCollectedAt: '',
    accessoryCollectedNotes: ''
  })
}

const resetBatchForm = () => {
  Object.assign(batchForm, {
    description: '',
    location: '',
    table: null,
    price: 0,
    quantity: 1,
    buyer: '',
    buyerDocument: '',
    buyerEmail: '',
    buyerPhone: '',
    order: '',
    salesEndDateTime: ''
  })
}

// Handle check-in status change
const onCheckedInChange = () => {
  if (ticketForm.checkedIn && !ticketForm.checkedInAt) {
    // If checking in and no date set, set current date/time
    ticketForm.checkedInAt = formatDateTimeLocalInput(new Date())
  } else if (!ticketForm.checkedIn) {
    // If unchecking, clear the check-in date
    ticketForm.checkedInAt = ''
  }
}

// Handle accessory pickup status change
const onAccessoryCollectedChange = () => {
  if (ticketForm.accessoryCollected && !ticketForm.accessoryCollectedAt) {
    // If marking as collected and no date set, set current date/time
    ticketForm.accessoryCollectedAt = formatDateTimeLocalInput(new Date())
  } else if (!ticketForm.accessoryCollected) {
    // If unmarking, clear the pickup date
    ticketForm.accessoryCollectedAt = ''
  }
}

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'No date'
  return new Date(dateString).toLocaleString()
}

// Store for cached confirmation URLs to avoid repeated API calls
const confirmationUrlCache = ref({})
const pendingUrls = new Set() // Track URLs being generated to prevent duplicate requests

const openConfirmationPage = async (orderId) => {
  if (!orderId || pendingUrls.has(orderId)) return

  if (confirmationUrlCache.value[orderId]) {
    window.open(confirmationUrlCache.value[orderId], '_blank', 'noopener')
    return
  }

  const targetWindow = window.open('', '_blank')
  if (targetWindow) {
    targetWindow.opener = null
    targetWindow.document.write('<!doctype html><title>Opening confirmation...</title><p>Opening confirmation...</p>')
    targetWindow.document.close()
  }
  pendingUrls.add(orderId)

  try {
    const response = await get(`/api/orders/${orderId}/confirmation-hash?eventId=${eventId.value}`)
    if (!response?.hash) {
      throw new Error('Confirmation hash was not returned')
    }

    const confirmationUrl = `${window.location.origin}/confirmation/${response.hash}`
    confirmationUrlCache.value[orderId] = confirmationUrl

    if (targetWindow) {
      targetWindow.location.href = confirmationUrl
    } else {
      window.open(confirmationUrl, '_blank', 'noopener')
    }
  } catch (err) {
    if (targetWindow) {
      targetWindow.close()
    }
    error.value = err?.message || 'Failed to open confirmation page'
  } finally {
    pendingUrls.delete(orderId)
  }
}

// Toggle ticket selection
const toggleTicketSelection = (ticketId) => {
  const index = selectedTicketIds.value.indexOf(ticketId)
  if (index > -1) {
    selectedTicketIds.value.splice(index, 1)
  } else {
    selectedTicketIds.value.push(ticketId)
  }
}

// Clear selection
const clearSelection = () => {
  selectedTicketIds.value = []
}

// Show bulk edit modal
const showBulkEditModal = () => {
  resetBulkEditForm()
  isBulkEditModalOpen.value = true
}

// Save bulk edit
const saveBulkEdit = async () => {
  try {
    // Build updates object with only filled fields or cleared fields
    const updates = {}
    
    // Handle location
    if (bulkEditForm.clearLocation) {
      updates.location = null
    } else if (bulkEditForm.location) {
      updates.location = bulkEditForm.location
    }
    
    // Handle table
    if (bulkEditForm.clearTable) {
      updates.table = null
    } else if (bulkEditForm.table) {
      updates.table = parseInt(bulkEditForm.table)
    }
    
    // Handle order
    if (bulkEditForm.clearOrder) {
      updates.order = null
    } else if (bulkEditForm.order) {
      updates.order = bulkEditForm.order
    }
    
    // Handle buyer
    if (bulkEditForm.clearBuyer) {
      updates.buyer = null
    } else if (bulkEditForm.buyer) {
      updates.buyer = bulkEditForm.buyer
    }
    
    // Handle buyerDocument
    if (bulkEditForm.clearBuyerDocument) {
      updates.buyerDocument = null
    } else if (bulkEditForm.buyerDocument) {
      updates.buyerDocument = bulkEditForm.buyerDocument
    }
    
    // Handle buyerEmail
    if (bulkEditForm.clearBuyerEmail) {
      updates.buyerEmail = null
    } else if (bulkEditForm.buyerEmail) {
      updates.buyerEmail = bulkEditForm.buyerEmail
    }

    if (bulkEditForm.clearBuyerPhone) {
      updates.buyerPhone = null
    } else if (bulkEditForm.buyerPhone) {
      updates.buyerPhone = bulkEditForm.buyerPhone
    }
    
    // Handle checkedIn
    if (bulkEditForm.checkedIn !== undefined) {
      updates.checkedIn = bulkEditForm.checkedIn
    }
    
    // Handle checkedInAt
    if (bulkEditForm.checkedInAt) {
      updates.checkedInAt = serializeDateTimeLocalInput(bulkEditForm.checkedInAt)
    }
    
    // Handle accessoryCollected
    if (bulkEditForm.accessoryCollected !== undefined) {
      updates.accessoryCollected = bulkEditForm.accessoryCollected
    }
    
    // Handle accessoryCollectedAt
    if (bulkEditForm.accessoryCollectedAt) {
      updates.accessoryCollectedAt = serializeDateTimeLocalInput(bulkEditForm.accessoryCollectedAt)
    }

    // Handle accessoryCollectedNotes
    if (bulkEditForm.accessoryCollectedNotes) {
      updates.accessoryCollectedNotes = bulkEditForm.accessoryCollectedNotes
    }
    
    if (Object.keys(updates).length === 0) {
      error.value = 'Please fill at least one field to update or check a "Clear" option'
      return
    }
    
    const result = await post('/api/tickets/bulk-edit', {
      ticketIds: selectedTicketIds.value,
      updates
    })
    
    if (result.success) {
      // Close modal and reload
      isBulkEditModalOpen.value = false
      resetBulkEditForm()
      clearSelection()
      await loadTickets()
      await loadGroups()
      await loadStats()
      error.value = null
    }
  } catch (err) {
    console.error('Failed to bulk edit tickets:', err)
    error.value = err.message || 'Failed to bulk edit tickets'
  }
}

// Confirm bulk delete
const confirmBulkDelete = () => {
  const count = selectedTicketIds.value.length
  if (confirm(`Are you sure you want to delete ${count} ticket(s)? This action cannot be undone.`)) {
    performBulkDelete()
  }
}

// Perform bulk delete
const performBulkDelete = async () => {
  try {
    const result = await post('/api/tickets/bulk-delete', {
      ticketIds: selectedTicketIds.value
    })
    
    if (result.success) {
      clearSelection()
      await loadTickets()
      await loadGroups()
      await loadStats()
      error.value = null
    }
  } catch (err) {
    console.error('Failed to bulk delete tickets:', err)
    error.value = err.message || 'Failed to bulk delete tickets'
  }
}

// Reset bulk edit form
const resetBulkEditForm = () => {
  Object.assign(bulkEditForm, {
    location: '',
    table: '',
    order: '',
    buyer: '',
    buyerDocument: '',
    buyerEmail: '',
    buyerPhone: '',
    checkedIn: false,
    checkedInAt: '',
    accessoryCollected: false,
    accessoryCollectedAt: '',
    accessoryCollectedNotes: '',
    clearLocation: false,
    clearTable: false,
    clearOrder: false,
    clearBuyer: false,
    clearBuyerDocument: false,
    clearBuyerEmail: false,
    clearBuyerPhone: false
  })
}

// Load data on mount
onMounted(async () => {
  if (isAuthenticated.value) {
    await Promise.all([
      loadEvent(),
      loadTickets(),
      loadGroups(),
      loadStats()
    ])
  }
})
</script>

<style scoped>
.event-detail-description {
  display: -webkit-box;
  line-clamp: 3;
  max-height: 5.25rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
</style>
